/**
 * Label for the *viewer's* timezone, spelled out.
 *
 * The portal converts every stored UTC timestamp to the viewer's local time, so
 * this is deliberately their zone and not the event's. Someone in Denver sees
 * "Mountain Time" while someone in New York sees "Eastern Time".
 *
 * 'longGeneric' gives the plain name with no daylight/standard wording, which is
 * what the client asked for in place of "MDT".
 *
 * This reads the timeZoneName part directly rather than slicing up a formatted
 * date string. The previous approach used /.*\s(.+)/ against toLocaleDateString
 * output, which is greedy and would return only "Time" from "Mountain Time".
 */
export function getTimeZoneLabel(date: Date = new Date()): string {
  const read = (style: "longGeneric" | "short"): string => {
    try {
      const parts = new Intl.DateTimeFormat(navigator.language, {
        timeZoneName: style,
        // Intl needs at least one date or time component alongside the zone.
        year: "numeric",
      } as Intl.DateTimeFormatOptions).formatToParts(date);

      const zone = parts.find((part) => part.type === "timeZoneName");

      return zone && zone.value ? zone.value : "";
    } catch (e) {
      // An engine that does not know the requested style throws a RangeError.
      return "";
    }
  };

  // Engines that silently ignore 'longGeneric' emit the short label instead,
  // which is the same thing the fallback would produce, so either is fine.
  return read("longGeneric") || read("short");
}
